/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Icon,
  IconButton,
  Modal,
  RoundIcon,
  TabHead,
  TabLabel,
  TabPanel,
  TabProvider,
  toasterService
} from '@hevoio/hevo-ui-toolkit';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import AlertDialogV2 from '../../../modal/components/alert-dialog/AlertDialogV2';
import { HdCoordinatedHeight } from '../../../layout/components/coordinated-height/HdCoordinatedHeight';
import useUserService from '../../../auth/hooks/useUserService';
import useTeamSettings from '../hooks/useTeamSettingsService';
import useService from '../../../injector/hooks/useService';
import { UserService } from '../../../core/services/user.service';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { RolesFactory } from '../factory';
import { Role } from '../models';
import TeamAPI from '../api/TeamAPI';
import { InviteActionResponse, MemberPageURLBasedAction } from './models';
import styles from './styles.module.scss';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { AnalyticsSessionProvider } from '../../../analytics/context/AnalyticsContext';
import { TeamMembersTab } from './TeamMembersTab';
import { WorkspaceMembersTab } from './WorkspaceMembersTab';

const MembersTab = {
  TEAM: 'team',
  WORKSPACES: 'workspaces'
} as const;

export interface MembersProps {
  savedTeamName: string;
  inviteActionInitialResponse: InviteActionResponse;
}

const tabPanelClass = styles.tabPanel;

export function Members({ inviteActionInitialResponse }: MembersProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentTab =
    searchParams.get('tab') === MembersTab.WORKSPACES ? MembersTab.WORKSPACES : MembersTab.TEAM;

  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const { user } = useUserService();
  const { isWorkspacesEnabled, isISVTeam } = useTeamSettings();
  const userService = useService(UserService);
  const [teamName, setTeamName] = useState<string>(user.teamName);
  const [inviteActionFailureRetryVisible, setAcceptInviteFailureRetryVisible] = useState(
    !!inviteActionInitialResponse?.error
  );

  const { endSession, startSession } = useAnalyticsTracker();

  const [acceptInviteSuccessVisible, setAcceptInviteSuccessVisible] = useState(
    !inviteActionInitialResponse?.error &&
      inviteActionInitialResponse?.action === MemberPageURLBasedAction.ACCEPT_INVITE
  );
  const [declineInviteSuccessVisible, setDeclineInviteSuccessVisible] = useState(
    !inviteActionInitialResponse?.error &&
      inviteActionInitialResponse?.action === MemberPageURLBasedAction.DECLINE_INVITE
  );
  const [inviteActionResponse, setInviteActionResponse] = useState(inviteActionInitialResponse);

  const coordinatedHeightTargetRef = useRef(null);

  useEffect(() => {
    startSession();
    return () => endSession();
  }, []);

  const fetchRoles = useCallback(() => {
    setRolesLoading(true);
    TeamAPI.getTeamRoles()
      .then(res => setRoles(RolesFactory(res.data.roles)))
      .catch(err =>
        toasterService.pop(
          'error',
          undefined,
          getErrorMessageFromObj(err) || 'Failed to load roles'
        )
      )
      .finally(() => setRolesLoading(false));
  }, []);

  useEffect(() => {
    fetchRoles();
  }, []);

  const onInviteActionFailureRetryDialogClose = (response: InviteActionResponse) => {
    if (response?.action === MemberPageURLBasedAction.ACCEPT_INVITE) {
      setAcceptInviteSuccessVisible(true);
      setInviteActionResponse(response);
    } else if (response?.action === MemberPageURLBasedAction.DECLINE_INVITE) {
      setDeclineInviteSuccessVisible(true);
      setInviteActionResponse(response);
    }

    setAcceptInviteFailureRetryVisible(false);
  };

  return (
    <HdCoordinatedHeight elRef={coordinatedHeightTargetRef.current}>
      <div className='box box--table box--fixed-height' ref={coordinatedHeightTargetRef}>
        <TabProvider
          currentTab={currentTab}
          onClick={value =>
            navigate({ search: new URLSearchParams({ tab: value as string }).toString() })
          }
        >
          <TabHead compact isScrollable>
            <TabLabel compact value={MembersTab.TEAM} label='Team' data-id='members-tab-team' />
            {isWorkspacesEnabled && isISVTeam ? (
              <TabLabel
                compact
                value={MembersTab.WORKSPACES}
                label='Workspaces'
                data-id='members-tab-workspaces'
              />
            ) : null}
          </TabHead>

          {/* ── Team tab ── */}
          <TabPanel value={MembersTab.TEAM} activeTabClassName={tabPanelClass} keepMounted>
            <TeamMembersTab
              roles={roles}
              rolesLoading={rolesLoading}
              teamName={teamName}
              onTeamNameUpdate={name => {
                setTeamName(name);
                userService.updateTeamNameInStorage(name);
              }}
            />
          </TabPanel>

          {/* ── Workspaces tab ── */}
          <TabPanel value={MembersTab.WORKSPACES} activeTabClassName={tabPanelClass} keepMounted>
            <WorkspaceMembersTab roles={roles} />
          </TabPanel>
        </TabProvider>
      </div>

      <Modal
        open={inviteActionFailureRetryVisible}
        onClose={() => onInviteActionFailureRetryDialogClose(undefined)}
      >
        <InviteActionFailureRetryDialogContent
          dialogBodyData={inviteActionResponse}
          hideDialog={onInviteActionFailureRetryDialogClose}
        />
      </Modal>

      <AlertDialogV2
        open={acceptInviteSuccessVisible}
        title='Request has been approved!'
        body={() => (
          <AcceptInviteSuccessContent teamName={teamName} inviteActionData={inviteActionResponse} />
        )}
        iconName='checked-tick'
        iconContainerClass='success'
        positiveBtnText='Okay'
        onClose={() => {
          setAcceptInviteSuccessVisible(false);
        }}
      />

      <AlertDialogV2
        open={declineInviteSuccessVisible}
        title='Request has been declined!'
        body={() => (
          <DeclineInviteSuccessContent
            teamName={teamName}
            inviteActionData={inviteActionResponse}
          />
        )}
        iconName='close'
        iconContainerClass='error'
        positiveBtnText='Okay'
        onClose={() => {
          setDeclineInviteSuccessVisible(false);
        }}
      />
    </HdCoordinatedHeight>
  );
}

function AcceptInviteSuccessContent({
  teamName,
  inviteActionData
}: {
  teamName: string;
  inviteActionData: InviteActionResponse;
}) {
  return (
    <>
      {`${inviteActionData.userName}'s `}
      <span className='text-primary text-medium'>({inviteActionData.userEmail})</span> request to
      join the workspace <b>{teamName}</b> on Hevo has been approved.
    </>
  );
}

function DeclineInviteSuccessContent({
  teamName,
  inviteActionData
}: {
  teamName: string;
  inviteActionData: InviteActionResponse;
}) {
  return (
    <>
      {`${inviteActionData.userName}'s `}
      <span className='text-primary text-medium'>({inviteActionData.userEmail})</span> request to
      join the workspace <b>{teamName}</b> on Hevo has been declined.
    </>
  );
}

function InviteActionFailureRetryDialogContent({
  dialogBodyData,
  hideDialog
}: {
  dialogBodyData: InviteActionResponse;
  hideDialog: (data?: any) => void;
}) {
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  const accept = () => {
    setAccepting(true);

    TeamAPI.inviteAction(dialogBodyData.code, MemberPageURLBasedAction.ACCEPT_INVITE)
      .then(res => {
        setAccepting(false);
        hideDialog(res);
      })
      .catch(() => {
        setAccepting(false);
      });
  };

  const decline = () => {
    setDeclining(true);

    TeamAPI.inviteAction(dialogBodyData.code, MemberPageURLBasedAction.DECLINE_INVITE)
      .then(res => {
        setDeclining(false);
        hideDialog(res);
      })
      .catch(() => {
        setDeclining(false);
      });
  };

  const dataIdGenerator = getDataIdGenerator('membersInviteMemberDialog');

  return (
    <>
      <div className='dialog-header dialog-header--absolute' tabIndex={0}>
        <IconButton
          id='close_alert_dialog'
          onClick={() => hideDialog()}
          dataId={dataIdGenerator('close')}
          label='Close'
        >
          <Icon name='close' />
        </IconButton>
      </div>

      <div className='dialog-body p-6' data-id={dataIdGenerator('content')}>
        <div className='center-flex-row justify-center mb-5'>
          <RoundIcon dataId='' containerBg='error' iconName='error-filled' />
        </div>

        <div className='center-flex-col'>
          <div className='text-subheading-3 mb-2'>Sorry, Something Went Wrong!</div>

          <div>{getErrorMessageFromObj(dialogBodyData.error)}</div>

          {![404, 403].includes(dialogBodyData.error.status) && (
            <div className='mt-7'>
              <Button
                disabled={accepting || declining}
                showProgress={accepting}
                onClick={() => {
                  accept();
                }}
                dataId={dataIdGenerator('accept')}
              >
                Accept
              </Button>

              <Button
                disabled={accepting || declining}
                className='ml-3'
                variation='outline'
                showProgress={declining}
                onClick={() => {
                  decline();
                }}
                dataId={dataIdGenerator('decline')}
              >
                Decline
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function MembersWrapper(props: MembersProps) {
  return (
    <AnalyticsSessionProvider>
      <Members {...props} />
    </AnalyticsSessionProvider>
  );
}
